import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { User, AuthState, UserProfile } from '../types';
import { apiClient } from '../lib/api';
import { getPartnerAttributionFromUrl, cleanupAttributionParams, cleanupSignupTokenParam, getSignupTokenFromUrl, PartnerAttribution } from '../services/partnerAttribution';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  logout: () => Promise<void>;
  userProfile: UserProfile | null;
  /** `silent` reloads without flipping isLoading (no full-page loader) and keeps the
   * current profile if the reload fails. */
  refreshProfile: (options?: { silent?: boolean }) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  resetPassword: (password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  // Track users we've already attempted attribution for (prevents duplicate API calls)
  const attributionAttemptedRef = useRef<Set<string>>(new Set());

  /**
   * Create a referral if attribution data is available.
   * Priority 1: URL params (OAuth flow — params survived redirect via wildcard config)
   * Priority 2: user_metadata (email flow — cross-device confirmation)
   */
  const createReferralIfNeeded = async (session: Session) => {
    const userId = session.user.id;
    if (attributionAttemptedRef.current.has(userId)) {
      return;
    }
    attributionAttemptedRef.current.add(userId);

    // Priority 1: URL params (OAuth flow — params survived redirect)
    let attribution: PartnerAttribution | null = getPartnerAttributionFromUrl();

    // Priority 2: user_metadata (email flow — cross-device confirmation)
    if (!attribution) {
      const meta = session.user.user_metadata;
      if (meta?.partner_code) {
        attribution = {
          partner_code: meta.partner_code,
          attribution_source: meta.attribution_source || 'url',
          utm_source: meta.utm_source,
          utm_medium: meta.utm_medium,
          utm_campaign: meta.utm_campaign,
          landing_page: meta.landing_page,
          referrer_url: meta.referrer_url,
        };
      }
    }

    if (!attribution) {
      return;
    }

    try {
      await apiClient.recordReferral({
        partner_code: attribution.partner_code,
        attribution_source: attribution.attribution_source,
        utm_source: attribution.utm_source,
        utm_medium: attribution.utm_medium,
        utm_campaign: attribution.utm_campaign,
        landing_page: attribution.landing_page,
        referrer_url: attribution.referrer_url,
      });
    } catch (error) {
      console.error('Error creating referral:', error);
    }

    cleanupAttributionParams();
  };

  const loadUserProfile = async (userId: string, silent = false) => {
    try {
      const { data: profile, error } = await supabase
        .from('user_profiles')
        // embed pinned to the org_id FK so a future second organizations FK
        // can't make PostgREST return PGRST201 (ambiguous embed)
        .select('*, organizations:org_id(is_client_workspace)')
        .eq('user_id', userId)
        .maybeSingle();
      if (error) {
        // Fails OPEN for routing (client would land on /datasets until reload) —
        // logged so a broken embed is diagnosable, never silent.
        console.error('Profile load failed (client-routing gate degrades open):', error);
      }

      // A silent reload never blanks a profile the user is looking at.
      if (!silent || (!error && profile)) setUserProfile(profile);

      // Only set loading to false after profile is loaded (or failed to load)
      setAuthState(prev => ({
        ...prev,
        isLoading: false
      }));
    } catch (error) {
      console.error('Error loading user profile:', error);

      // Even if profile loading fails, we should stop the loading state
      setAuthState(prev => ({
        ...prev,
        isLoading: false
      }));
    }
  };

  const refreshProfile = async ({ silent = false }: { silent?: boolean } = {}) => {
    if (authState.user) {
      if (silent) {
        // In-place sync (e.g. Settings edits): no loader, so the page stays mounted.
        await loadUserProfile(authState.user.id, true);
        return;
      }
      // Set loading state when refreshing profile (e.g., after onboarding completion)
      setAuthState(prev => ({
        ...prev,
        isLoading: true
      }));
      await loadUserProfile(authState.user.id);
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        const user = mapSupabaseUser(session.user);
        setAuthState({
          user,
          isAuthenticated: true,
          isLoading: true, // Keep loading true until profile is loaded
        });
        loadUserProfile(user.id);
      } else {
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false, // No user, so no profile to load
        });
        setUserProfile(null);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session?.user) {
          const newUser = mapSupabaseUser(session.user);

          // Only update state if user data actually changed
          setAuthState(prevState => {
            const currentUser = prevState.user;
            // ID (user switch) and isAdmin: a TOKEN_REFRESHED carrying a new
            // is_admin claim must land — the SKY-507 gate keys on it, and
            // discarding it strands a freshly-granted operator on the waitlist
            // until they log out (658 review P2).
            const userChanged = !currentUser ||
              currentUser.id !== newUser.id ||
              currentUser.isAdmin !== newUser.isAdmin ||
              currentUser.signupToken !== newUser.signupToken;

            if (!userChanged && prevState.isAuthenticated) {
              // User hasn't changed and is already authenticated, don't update
              return prevState;
            }

            // Only load profile if user actually changed
            if (userChanged) {
              loadUserProfile(newUser.id);
            }

            return {
              user: newUser,
              isAuthenticated: true,
              isLoading: userChanged ? true : prevState.isLoading,
            };
          });

          // Create referral from URL params or user_metadata on sign in
          if (event === 'SIGNED_IN') {
            createReferralIfNeeded(session);
            storeSignupTokenIfNeeded(session);
            // Create Stripe customer + free plan for genuinely new signups only.
            // Heuristic: created_at within 60s. Backend is idempotent (returns 400 if exists),
            // so clock skew or tab-restore double-fires are harmless — just noisy.
            const createdAt = new Date(session.user.created_at).getTime();
            if (Date.now() - createdAt < 60_000) {
              const meta = session.user.user_metadata;
              const displayName = [meta?.firstName, meta?.lastName].filter(Boolean).join(' ') || meta?.full_name || 'User';
              apiClient.createStripeCustomer(displayName).catch((err) => {
                console.warn('[Auth] Stripe customer creation failed (non-fatal):', err);
              });
            }
          }
        } else {
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
          setUserProfile(null);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  /** Store a conference link that arrived without going through signUp, so the claim sees it. */
  const storeSignupTokenIfNeeded = (session: Session): void => {
    const token = getSignupTokenFromUrl();
    if (!token || session.user.user_metadata?.signup_token === token) {
      cleanupSignupTokenParam();
      return;
    }
    supabase.auth
      .updateUser({ data: { signup_token: token } })
      .then(() => cleanupSignupTokenParam())
      .catch((error) => console.error('[Auth] Sign-up token not stored:', error));
  };

  const mapSupabaseUser = (supabaseUser: SupabaseUser): User => ({
    id: supabaseUser.id,
    email: supabaseUser.email || '',
    firstName: supabaseUser.user_metadata?.firstName || supabaseUser.user_metadata?.full_name?.split(' ')[0] || 'User',
    lastName: supabaseUser.user_metadata?.lastName || supabaseUser.user_metadata?.full_name?.split(' ').slice(1).join(' ') || '',
    isAdmin: supabaseUser.app_metadata?.is_admin || false,
    createdAt: new Date(supabaseUser.created_at),
    signupToken: supabaseUser.user_metadata?.signup_token || undefined,
  });

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      throw error;
    }
  };

  const signup = async (email: string, password: string, firstName: string, lastName: string) => {
    const attribution = getPartnerAttributionFromUrl();

    // Store attribution in user_metadata so it survives email confirmation on any device
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          firstName,
          lastName,
          // Stored on the account, not in localStorage, so confirming on another device keeps it.
          signup_token: getSignupTokenFromUrl(),
          partner_code: attribution?.partner_code || null,
          attribution_source: attribution?.attribution_source || null,
          utm_source: attribution?.utm_source || null,
          utm_medium: attribution?.utm_medium || null,
          utm_campaign: attribution?.utm_campaign || null,
          landing_page: attribution?.landing_page || null,
          referrer_url: attribution?.referrer_url || null,
        },
      },
    });

    if (error) {
      throw error;
    }

    if (attribution) {
      cleanupAttributionParams();
    }
  };

  const logout = async () => {
    // Use local scope to avoid CORS issues on production
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) {
      console.error('Logout error:', error);
      // Even if logout fails on server, clear local state
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      setUserProfile(null);
    }
    setUserProfile(null);
  };

  const requestPasswordReset = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      throw error;
    }
  };

  const resetPassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({
      password: password,
    });

    if (error) {
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ ...authState, login, signup, logout, userProfile, refreshProfile, requestPasswordReset, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};
